import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Check,
  MapPin,
  Clock,
  IndianRupee,
  ArrowRight,
  Upload,
  Mountain,
  TrendingUp,
  HeartHandshake
} from 'lucide-react';
import { careersAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildJobPostingSchema, buildBreadcrumbSchema } from '../utils/seo';
import Breadcrumbs from '../components/Breadcrumbs';
import LoadingIndicator from '../components/LoadingIndicator';
import './JobApplication.css';


const WHY_JOIN = [
  {
    title: 'Travel Opportunities',
    desc: 'Explore the Himalayas and remote locations',
    Icon: Mountain
  },
  {
    title: 'Career Growth',
    desc: 'Fast-track leadership opportunities',
    Icon: TrendingUp
  },
  {
    title: 'Make an Impact',
    desc: 'Empower youth through adventure',
    Icon: HeartHandshake
  }
];

const HIRING_STEPS = [
  { label: 'Applied', desc: 'Review of your application' },
  { label: 'Interview', desc: 'Discussion with the team' },
  { label: 'Hired', desc: 'Welcome aboard' }
];

const STORAGE_KEY = (jobId) => `job_application_${jobId}`;
const STATUS_ORDER = { applied: 0, interview: 1, hired: 2 };
const MOBILE_DIGITS = 10;

const normalizeMobile = (value) => String(value || '').replace(/\D/g, '').slice(0, MOBILE_DIGITS);
const isValidMobile = (value) => normalizeMobile(value).length === MOBILE_DIGITS;

const JobApplication = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formStep, setFormStep] = useState(1);
  const { settings } = useSiteSettings();

  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';

  const jobSchema = useMemo(() => {
    if (!job) return null;
    const posting = buildJobPostingSchema(job, settings, publicBase);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'Careers', url: '/careers' },
      { name: job.title || 'Job Opening', url: `/careers/jobs/${id}` }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [posting, breadcrumbs].filter(Boolean)
    };
  }, [job, settings, publicBase, id]);

  usePageSeo({
    enabled: Boolean(job),
    title: job?.title ? `${job.title} | Careers at ${brand}` : `Careers | ${brand}`,
    description: job?.description ? String(job.description).slice(0, 160) : 'Join our team at Invincible NGO.',
    canonical: id && publicBase ? `${publicBase}/careers/jobs/${id}` : undefined,
    schema: jobSchema
  });

  const MAX_RESUME_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    gender: '',
    linkedin: '',
    hearAbout: '',
    referredBy: '',
    experience: '',
    currentCtc: '',
    expectedCtc: '',
    resume: null
  });
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    careersAPI.getJobById(id)
      .then((res) => {
        const data = res.data?.data || res.data;
        if (data && !cancelled) setJob(data);
      })
      .catch(() => { if (!cancelled) setJob(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!id || !job) return;
    const raw = localStorage.getItem(STORAGE_KEY(id));
    if (!raw) return;
    let stored;
    try {
      stored = JSON.parse(raw);
    } catch {
      return;
    }
    const applicationId = stored?.applicationId;
    if (!applicationId) return;
    setApplicationStatus((prev) => prev || { status: 'applied' });
    let cancelled = false;
    careersAPI.getApplicationStatus(applicationId)
      .then((res) => {
        const data = res.data?.data || res.data;
        if (data && !cancelled) setApplicationStatus({ status: data.status, appliedAt: data.appliedAt });
      })
      .catch(() => {
        if (!cancelled) {
          localStorage.removeItem(STORAGE_KEY(id));
          setApplicationStatus(null);
        }
      });
    return () => { cancelled = true; };
  }, [id, job]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, phone: normalizeMobile(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (file) => {
    if (!file) return;
    const mime = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    const allowedMime =
      /^(application\/pdf|application\/msword|application\/vnd.openxmlformats-officedocument.wordprocessingml.document)$/;
    const allowedExt = /\.(pdf|doc|docx)$/;
    if (!allowedMime.test(mime) && !allowedExt.test(name)) {
      alert('Only PDF, DOC, or DOCX files are allowed.');
      return;
    }
    if (file.size > MAX_RESUME_SIZE_BYTES) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      alert(`Resume file size exceeds the 5 MB limit (selected file is ${sizeMb} MB). Please upload a file under 5 MB.`);
      return;
    }
    setFormData((prev) => ({ ...prev, resume: file }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const handleNextStep = () => {
    if (!formData.fullName.trim()) {
      alert('Please enter your full name.');
      return;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      alert('Please enter a valid email address.');
      return;
    }
    if (!isValidMobile(formData.phone)) {
      alert('Please enter a valid 10-digit mobile number.');
      return;
    }
    if (!formData.gender) {
      alert('Please select your gender.');
      return;
    }
    if (!formData.hearAbout) {
      alert('Please select where you heard about this job opening.');
      return;
    }
    setFormStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formStep !== 2) return;
    if (!isValidMobile(formData.phone)) {
      alert('Please enter a valid 10-digit mobile number.');
      setFormStep(1);
      return;
    }
    if (!formData.resume) {
      alert('Please upload your resume.');
      return;
    }
    if (formData.resume instanceof File && formData.resume.size > MAX_RESUME_SIZE_BYTES) {
      alert('Resume file size must be less than 5 MB.');
      return;
    }
    setSubmitting(true);
    try {
      let resumeUrl = '';
      if (formData.resume instanceof File) {
        const up = await careersAPI.uploadResume(formData.resume);
        resumeUrl = up.data?.data?.url || '';
      }
      const res = await careersAPI.submitApplication(id, {
        fullName: formData.fullName.trim(),
        email: formData.email.trim(),
        phone: normalizeMobile(formData.phone) || undefined,
        gender: formData.gender || undefined,
        linkedin: formData.linkedin?.trim() || undefined,
        hearAbout: formData.hearAbout || undefined,
        referredBy: formData.referredBy?.trim() || undefined,
        experience: formData.experience != null && String(formData.experience).trim() !== '' ? String(formData.experience).trim() : undefined,
        currentCtc: formData.currentCtc != null && String(formData.currentCtc).trim() !== '' ? String(formData.currentCtc).trim() : undefined,
        expectedCtc: formData.expectedCtc != null && String(formData.expectedCtc).trim() !== '' ? String(formData.expectedCtc).trim() : undefined,
        resumeUrl
      });
      const applicationId = res.data?.data?.applicationId;
      if (applicationId) {
        localStorage.setItem(STORAGE_KEY(id), JSON.stringify({ applicationId }));
      }
      setApplicationStatus({ status: 'applied', appliedAt: new Date().toISOString() });
      alert('Thank you! Your application has been submitted.');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const responsibilitiesList = job?.responsibilitiesList || (job?.responsibilities ? (() => { try { return JSON.parse(job.responsibilities); } catch { return []; } })() : []);
  const requirementsList = job?.requirementsList || (job?.requirements ? (() => { try { return JSON.parse(job.requirements); } catch { return []; } })() : []);

  if (loading) {
    return (
      <div className="job-application-page">
        <div className="job-app-loading">
          <LoadingIndicator label="Loading..." />
        </div>
      </div>
    );
  }
  if (!job) return <div className="job-application-page"><div className="job-app-error">Job not found. <Link to="/careers">Back to Open Positions</Link></div></div>;

  return (
    <div className="job-application-page">
      <div className="job-app-container">
        <Breadcrumbs
          items={[
            { label: 'Careers', path: '/careers' },
            { label: job.title || 'Job Opening' }
          ]}
          className="job-app-breadcrumbs"
        />
        <Link to="/careers" className="job-app-back">← Back to Open Positions</Link>

        <header className="job-app-header">
          <h1 className="job-app-title">{job.title}</h1>
          <div className="job-app-tags">
            {job.location && (
              <span className="job-app-tag"><MapPin size={16} /> Location: {job.location}</span>
            )}
            {job.employmentType && (
              <span className="job-app-tag"><Clock size={16} /> Type: {job.employmentType}</span>
            )}
            <span className="job-app-tag"><IndianRupee size={16} /> Salary: {job.salary || 'Competitive'}</span>
          </div>
        </header>

        <div className="job-app-layout">
          <main className="job-app-main">
            {job.description && (
              <section className="job-app-section">
                <h2>About the Role</h2>
                <p className="job-app-description">{job.description}</p>
              </section>
            )}

            {responsibilitiesList.length > 0 && (
              <section className="job-app-section">
                <h2>Key Responsibilities</h2>
                <ul className="job-app-list">
                  {responsibilitiesList.map((item, i) => (
                    <li key={i}><Check size={18} className="job-app-check" /><span>{item}</span></li>
                  ))}
                </ul>
              </section>
            )}

            {requirementsList.length > 0 && (
              <section className="job-app-section">
                <h2>Requirements</h2>
                <ul className="job-app-list">
                  {requirementsList.map((item, i) => (
                    <li key={i}><Check size={18} className="job-app-check" /><span>{item}</span></li>
                  ))}
                </ul>
              </section>
            )}

            <section className="job-app-section job-app-form-section">
              {applicationStatus ? (
                <div className="job-app-submitted">
                  <h2>Application submitted</h2>
                  <p className="job-app-submitted-msg">Thank you! Your application has been submitted. We will review it and get back to you.</p>
                  <p className="job-app-submitted-hint">Track your status in the Hiring Process card on the right.</p>
                </div>
              ) : (
                <>
                  <h2>Apply for this Position</h2>
                  <div className="job-app-steps">
                    <button type="button" className={`job-app-step ${formStep === 1 ? 'active' : ''}`} onClick={() => setFormStep(1)}>1 Personal Details</button>
                    <button type="button" className={`job-app-step ${formStep === 2 ? 'active' : ''}`} onClick={() => setFormStep(2)}>2 Experience & Resume</button>
                  </div>

                  <form onSubmit={handleSubmit} className="job-app-form">
                    {formStep === 1 && (
                      <div className="job-app-form-step">
                        <div className="job-app-form-group">
                          <label>Full Name *</label>
                          <input
                            type="text"
                            name="fullName"
                            value={formData.fullName}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            required
                          />
                        </div>
                        <div className="job-app-form-group">
                          <label>Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="you@example.com"
                            required
                          />
                        </div>
                        <div className="job-app-form-group">
                          <label>Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="10-digit mobile number"
                            inputMode="numeric"
                            maxLength={MOBILE_DIGITS}
                            required
                          />
                        </div>
                        <div className="job-app-form-group">
                          <label>Gender *</label>
                          <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Prefer not to say">Prefer not to say</option>
                          </select>
                        </div>
                        <div className="job-app-form-group">
                          <label>LinkedIn/Instagram Profile</label>
                          <input
                            type="text"
                            name="linkedin"
                            value={formData.linkedin}
                            onChange={handleChange}
                            placeholder="https://linkedin.com/in/... or https://instagram.com/in/..."
                          />
                        </div>
                        <div className="job-app-form-group">
                          <label>Where did you hear about the Job? *</label>
                          <select
                            name="hearAbout"
                            value={formData.hearAbout}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select an option</option>
                            <option value="LinkedIn">LinkedIn</option>
                            <option value="Instagram">Instagram</option>
                            <option value="Invincible Website">Invincible Website</option>
                            <option value="Friend / Colleague">Friend / Colleague</option>
                            <option value="WhatsApp">WhatsApp</option>
                            <option value="College / Campus Placement">College / Campus Placement</option>
                            <option value="Job Portal (Naukri, Indeed, etc.)">Job Portal (Naukri, Indeed, etc.)</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <div className="job-app-form-group">
                          <label>Any Reference or Referred By</label>
                          <input
                            type="text"
                            name="referredBy"
                            value={formData.referredBy}
                            onChange={handleChange}
                            placeholder="Name or details of person who referred you (if any)"
                          />
                        </div>
                        <button type="button" className="job-app-btn-next" onClick={handleNextStep}>
                          Next: Experience & Resume <ArrowRight size={18} />
                        </button>
                      </div>
                    )}

                    {formStep === 2 && (
                      <div className="job-app-form-step">
                        <div className="job-app-form-group">
                          <label>Total Years of Experience</label>
                          <input
                            type="number"
                            name="experience"
                            value={formData.experience}
                            onChange={handleChange}
                            min="0"
                            step="any"
                            placeholder="e.g. 2.5 (enter 0 for fresher)"
                          />
                        </div>
                        <div className="job-app-form-row">
                          <div className="job-app-form-group">
                            <label>Current CTC (₹ Lakhs / Year)</label>
                            <input
                              type="number"
                              name="currentCtc"
                              value={formData.currentCtc}
                              onChange={handleChange}
                              min="0"
                              step="any"
                              placeholder="e.g. 4.5 (enter 0 if fresher)"
                            />
                          </div>
                          <div className="job-app-form-group">
                            <label>Expected CTC (₹ Lakhs / Year)</label>
                            <input
                              type="number"
                              name="expectedCtc"
                              value={formData.expectedCtc}
                              onChange={handleChange}
                              min="0"
                              step="any"
                              placeholder="e.g. 6.0"
                            />
                          </div>
                        </div>
                        <div className="job-app-form-group">
                          <label>Upload Resume / CV *</label>
                          <div
                            className={`job-app-upload ${dragOver ? 'drag-over' : ''} ${formData.resume ? 'has-file' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            onClick={() => document.getElementById('job-resume-input')?.click()}
                          >
                            <input
                              id="job-resume-input"
                              type="file"
                              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              onChange={(e) => handleFile(e.target.files?.[0])}
                              className="job-app-upload-input"
                            />
                            {formData.resume ? (
                              <div className="job-app-upload-fileinfo">
                                <span className="job-app-upload-filename">{formData.resume.name}</span>
                                {formData.resume.size && (
                                  <span className="job-app-upload-filesize">
                                    ({(formData.resume.size / (1024 * 1024)).toFixed(2)} MB)
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="job-app-upload-change"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormData((prev) => ({ ...prev, resume: null }));
                                  }}
                                >
                                  Change file
                                </button>
                              </div>
                            ) : (
                              <>
                                <Upload size={32} className="job-app-upload-icon" />
                                <span>Drag and drop your resume here</span>
                                <span className="job-app-upload-hint">PDF, DOC, DOCX up to 5 MB</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="job-app-form-actions">
                          <button type="button" className="job-app-btn-back" onClick={() => setFormStep(1)}>Back</button>
                          <button type="submit" className="job-app-btn-submit" disabled={submitting}>
                            {submitting ? 'Submitting...' : 'Submit Application'} <ArrowRight size={18} />
                          </button>
                        </div>
                      </div>
                    )}
                  </form>
                </>
              )}
            </section>
          </main>

          <aside className="job-app-sidebar">
            <div className="job-app-sidebar-card">
              <h3>Why Join Us?</h3>
              {WHY_JOIN.map((item, i) => {
                const WIcon = item.Icon;
                return (
                  <div key={i} className="job-app-sidebar-item">
                    <span className="job-app-sidebar-icon" aria-hidden="true">
                      <WIcon size={20} strokeWidth={2} />
                    </span>
                    <div className="job-app-sidebar-item-text">
                      <strong>{item.title}</strong>
                      <p>{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="job-app-sidebar-card">
              <h3>Hiring Process</h3>
              {applicationStatus?.status === 'rejected' ? (
                <div className="job-app-process-rejected">
                  <p><strong>Better luck next time</strong></p>
                  <p>We have decided to move forward with other candidates for this role. We encourage you to apply for other open positions.</p>
                </div>
              ) : (
                <ol className="job-app-process-timeline">
                  {HIRING_STEPS.map((step, i) => {
                    const idx =
                      applicationStatus?.status != null
                        ? STATUS_ORDER[applicationStatus.status]
                        : undefined;
                    const isActive = typeof idx === 'number' ? idx === i : i === 0;
                    const isDone = typeof idx === 'number' && idx > i;
                    const isLast = i === HIRING_STEPS.length - 1;
                    return (
                      <li
                        key={i}
                        className={`job-app-process-step ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}
                      >
                        <div className="job-app-process-marker">
                          <span className="job-app-process-dot" />
                          {!isLast ? <span className="job-app-process-line" aria-hidden="true" /> : null}
                        </div>
                        <div className="job-app-process-body">
                          <strong>{step.label}</strong>
                          <p>{step.desc}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default JobApplication;
